import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { appearance } from 'src/redux/basic.slice';

export interface IOrgBranding {
    /** Org display name (appearance.web_title), fallback "Western Alliance". */
    name: string;
    /** Full URL of the uploaded brand logo, or null when the org hasn't uploaded one (callers fall back to the black WAL icon). */
    logoSrc: string | null;
    /** Org MC number from Organization Settings, or null when not set. */
    mcNumber: string | null;
}

/**
 * Pure mapper from raw appearance data to the org branding shape — for code that
 * runs OUTSIDE the redux Provider (e.g. map popups rendered via createRoot),
 * where the hook below cannot be used. Pass `store.getState().basic.appearance`.
 */
export const getOrgBrandingFromAppearance = (appearanceData: any, fallbackName = 'Western Alliance'): IOrgBranding => ({
    name: appearanceData?.web_title || fallbackName,
    // TEMPORARY: org logo from the appearance table is disabled — always null so every
    // caller renders its static black WAL icon fallback. Restore the line below to re-enable.
    // logoSrc: appearanceData?.logo && appearanceData?.logoUrl ? `${appearanceData.logoUrl}${appearanceData.logo}` : null,
    logoSrc: null,
    mcNumber: appearanceData?.mcNumber || null,
});

/**
 * Org identity from the shared appearance table (WAL Organization Settings),
 * loaded into redux by getUserDetails on login.
 *
 * Use this wherever the portal presents WAL itself as the carrier — carrier
 * visibility off, or no carrier assigned — so name/logo/MC number follow the
 * org settings instead of hardcoded "Western Alliance" + static icon.
 */
const useOrgBranding = (): IOrgBranding => {
    const { t } = useTranslation();
    const appearanceData = useSelector(appearance);

    return useMemo(() => getOrgBrandingFromAppearance(appearanceData, t('Common.Western_Alliance')), [appearanceData, t]);
};

export default useOrgBranding;
